import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import s from "./ManagersApplicationsList.module.scss";
import { Button, Input, Modal, Progress, Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CreditCardOutlined,
  DownloadOutlined,
  EditOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getApplicationDetails,
  getManagersApplicationDetails,
  searchManagersApplications,
  setLastSearchValue,
  setManagersActiveTab,
} from "../../redux/ApplicationSlice";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import Footer from "../../components/Footer/Footer";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";
import { getListStatusTag } from "../../utils/functions";
import MobileApplicationsList from "../MobileApplicationsList/MobileApplicationsList";
import {
  ICreateApplicationResponse,
  IDeclaration,
  IManagerEmployeeResponseResults,
} from "../../types";
import { getExchangeRate } from "../../redux/BillingSlice";
import { useTranslation } from "react-i18next";
import { getStatus, TDropdown } from "../../data";
import TabPane from "antd/es/tabs/TabPane";
import type { GetProps } from "antd";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
  getManagerApplicationDetails,
  getManagersApplicationsList,
  getOtherManagersApplicationsList,
  setCurrentManagerPageNumber,
  setCurrentOtherManagerPageNumber,
} from "../../redux/ManagerSlice";
var FileSaver = require("file-saver");

interface TableType {
  number: number;
  status: any;
  application_number: string;
  registration_date: string;
  user?: string;
}
export interface TableData {
  number: number;
  lrn_code: string;
  declaration: React.ReactNode;
  key: number;
  date: string;
}

type Props = {};
const { Search } = Input;
type SearchProps = GetProps<typeof Input.Search>;

const ManagersApplicationsList = (props: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const activeTab = useSelector(
    (state: RootState) => state.application.managersActiveTab,
  );
  const applicationsList = useSelector(
    (state: RootState) => state.manager.managersApplicationsList,
  );
  const user = useSelector((state: RootState) => state.user.user);
  const managerEmployees = useSelector(
    (state: RootState) => state.user.managerEmployeesList?.results,
  );
  const [data, setData] = useState<ICreateApplicationResponse[] | undefined>(
    applicationsList?.results,
  );
  const [selectedEmployee, setSelectedEmployee] = useState<number>();

  const currentStatePage = useSelector(
    (state: RootState) => state.manager.currentManagerPage,
  );
  const currentOtherStatePage = useSelector(
    (state: RootState) => state.manager.currentOtherManagerPage,
  );
  const [currentPage, setCurrentPage] = useState<number>(currentStatePage || 1);
  const [otherApplicationsCurrentPage, setOtherApplicationsCurrentPage] =
    useState<number>(currentOtherStatePage || 1);

  const [isDeclaratonModalOpen, setIsDeclarationModalOpen] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [progress, setProgress] = useState(0);

  const showModal = () => {
    setIsDeclarationModalOpen(true);
  };

  const handleOk = () => {
    setIsDeclarationModalOpen(false);
  };

  const handleCancel = () => {
    setIsDeclarationModalOpen(false);
  };

  const lang = useSelector((state: RootState) => state.application.lang);
  const { t } = useTranslation();
  const statusOptions = getStatus(t);

  const [pagination, setPagination] = useState({
    current: applicationsList?.pagination?.current_page || 1,
    pageSize: applicationsList?.pagination?.page_size || 30,
    total: applicationsList?.pagination?.total_items || 100,
  });

  const [otherApplicationsPagination, setOtherApplicationsPagination] =
    useState({
      current: applicationsList?.pagination?.current_page || 1,
      pageSize: applicationsList?.pagination?.page_size || 30,
      total: applicationsList?.pagination?.total_items || 100,
    });

  const currentPageRef = useRef(currentPage);
  const otherApplicationsCurrentPageRef = useRef(otherApplicationsCurrentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
    otherApplicationsCurrentPageRef.current = otherApplicationsCurrentPage;
  }, [currentPage, otherApplicationsCurrentPage]);

  useEffect(() => {
    const fetchData = async ({ page }: { page: number }) => {
      let res;

      if (activeTab === "myApplications") {
        res = await dispatch(getManagersApplicationsList(page || 1));

        if (
          res.payload &&
          res.type === "manager/getManagersApplicationsList/fulfilled" &&
          "results" in res.payload
        ) {
          setData(res.payload.results);
          setPagination({
            current: res.payload.pagination?.current_page,
            pageSize: res.payload.pagination?.page_size,
            total: res.payload.pagination?.total_items,
          });
        }
      }

      if (activeTab === "otherApplications") {
        res = await dispatch(getOtherManagersApplicationsList(page || 1));
        if (
          res.payload &&
          res.type === "manager/getOtherManagersApplicationsList/fulfilled" &&
          "results" in res.payload
        ) {
          setData(res.payload.results);
          setOtherApplicationsPagination({
            current: res.payload.pagination?.current_page,
            pageSize: res.payload.pagination?.page_size,
            total: res.payload.pagination?.total_items,
          });
        }
      }
    };

    fetchData({
      page:
        activeTab === "myApplications"
          ? currentPageRef.current
          : otherApplicationsCurrentPageRef.current,
    });

    const interval = setInterval(() => {
      const page =
        activeTab === "myApplications"
          ? currentPageRef.current
          : otherApplicationsCurrentPageRef.current;

      fetchData({ page });
    }, 100000);

    return () => clearInterval(interval);
  }, [dispatch, activeTab]);

  const getNextPage = async (page: number) => {
    if (activeTab === "myApplications") {
      setCurrentPage(page);
      dispatch(setCurrentManagerPageNumber(page));
      currentPageRef.current = page;

      if (page === undefined || page === null) return;

      try {
        const res = await dispatch(getManagersApplicationsList(page));
        if (!res.payload || !("results" in res.payload)) {
          console.error("Unexpected API response:", res);
          return;
        }

        if (res.type === "manager/getManagersApplicationsList/fulfilled") {
          if ("results" in res.payload) {
            setData(res.payload.results);
            setPagination({
              current: res?.payload?.pagination?.current_page,
              pageSize: res?.payload?.pagination.page_size,
              total: res.payload.pagination.total_items,
            });
          } else {
            console.error("Unexpected API response:", res.payload);
          }
        }
      } catch (error) {
        console.error("Error in getNextPage:", error);
      }
    }

    if (activeTab === "otherApplications") {
      setOtherApplicationsCurrentPage(page);
      dispatch(setCurrentOtherManagerPageNumber(page));
      otherApplicationsCurrentPageRef.current = page;

      if (page === undefined || page === null) return;
      try {
        const res = await dispatch(getOtherManagersApplicationsList(page));
        if (!res.payload || !("results" in res.payload)) {
          console.error("Unexpected API response:", res);
          return;
        }

        if (res.type === "manager/getOtherManagersApplicationsList/fulfilled") {
          if ("results" in res.payload) {
            setData(res.payload.results);
            setOtherApplicationsPagination({
              current: res?.payload?.pagination?.current_page,
              pageSize: res?.payload?.pagination.page_size,
              total: res.payload.pagination.total_items,
            });
          } else {
            console.error("Unexpected API response:", res.payload);
          }
        }
      } catch (error) {
        console.error("Error in getNextPage:", error);
      }
    }
  };

  const columns: ColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      width: 20,
      align: "center",
    },
    {
      title: t("Статус"),
      dataIndex: `status`,
      width: 350,
    },

    {
      title: t("Номер заявки"),
      dataIndex: `application_number`,
      width: 200,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("№ транспортного средства"),
      dataIndex: `vehicle_number`,
      width: 250,
      align: "center",
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Дата регистрации"),
      dataIndex: `registration_date`,
      align: "center",
      width: 150,
      render: (text) => <div className={s.font}>{text}</div>,
    },
    {
      title: t("Состояние заказа"),
      dataIndex: `order_status`,
      align: "center",
      width: 200,
      render: (text) => <div className={s.status_bar2}>{text}</div>,
      onCell: () => ({}),
    },
    {
      title: t("Просмотр"),
      dataIndex: `application`,
      width: 50,
    },
  ];

  const getPaymentAmount = async (id: number) => {
    const res = await dispatch(getApplicationDetails(id));
    if (res.type === "applications/getApplicationDetails/fulfilled") {
      navigate(`/payment/${id}`);
    }
  };

  const onSearch: SearchProps["onSearch"] = async (value, _e, info) => {
    const res = await dispatch(
      searchManagersApplications({ search_term: value, page: 1 }),
    );

    dispatch(setLastSearchValue(value));
    console.log("res", res);
    if (res.type === "applications/searchManagersApplications/fulfilled") {
      navigate("/search-results");
    }
  };
  const [declarations, setDeclarations] = useState<IDeclaration[]>([]);

  //   const getDeclarations = async (id: number) => {
  //     if (activeTab === "all") {
  //       const res = applicationsList?.results.find(
  //         (it) => it.id === id
  //       )?.declarations;

  //       if (res) setDeclarations(res);
  //     }
  //   };
  async function downloadDeclarationsAsZip() {
    setLoadingFiles(true);
    setProgress(0);

    const zip = new JSZip();

    if (declarations.length) {
      const total = declarations.filter((d) => d.declaration_file).length;

      let completed = 0;

      for (const item of declarations) {
        {
          try {
            const response = await fetch(item.declaration_file);
            if (!response.ok) {
              console.error(`Ошибка загрузки ${item.declaration_file}`);
              continue;
            }

            const blob = await response.blob();
            const filename =
              item.name || item.declaration_file.split("/").pop() || "file.pdf";

            zip.file(filename, blob);

            completed++;
            setProgress(Math.round((completed / total) * 80));
          } catch (error) {
            console.error(
              "Ошибка загрузки файла:",
              item.declaration_file,
              error,
            );
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" }, (metadata) => {
        const percent = 80 + (metadata.percent / 100) * 20;
        setProgress(Math.round(percent));
      });

      saveAs(zipBlob, "declarations.zip");
      setProgress(100);
    }
    setLoadingFiles(false);
  }

  const downloadFile = async (file: any) => {
    if (!file) return;
    try {
      if (file.declaration_file) {
        const response = await fetch(file.declaration_file);
        if (!response.ok) throw new Error("Failed to fetch the file");
        const fileBlob = await response.blob();
        const mimeType = file.type || getMimeTypeByExtension(file.name);
        FileSaver.saveAs(fileBlob, file.name);
      }
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };
  const getMimeTypeByExtension = (filename: string) => {
    const extension = filename.split(".").pop()?.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      doc: "application/msword",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      jpeg: "image/jpeg",
      jpg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      txt: "text/plain",
      csv: "text/csv",
      zip: "application/zip",
    };
  };

  const onTabChange = async (tab: string) => {
    dispatch(setManagersActiveTab(tab));

    if (tab === "myApplications") {
      const res = await dispatch(getManagersApplicationsList(1));

      if (
        res.payload &&
        "results" in res.payload &&
        res.type === "manager/getManagersApplicationsList/fulfilled"
      ) {
        setData(res.payload.results);
        setPagination({
          current: res.payload.pagination?.current_page,
          pageSize: res.payload.pagination?.page_size,
          total: res.payload.pagination?.total_items,
        });
      }
    }

    if (tab === "otherApplications") {
      const res = await dispatch(getOtherManagersApplicationsList(1));
      if (
        res.payload &&
        "results" in res.payload &&
        res.type === "manager/getOtherManagersApplicationsList/fulfilled"
      ) {
        setData(res.payload.results);
        setOtherApplicationsPagination({
          current: res.payload.pagination?.current_page,
          pageSize: res.payload.pagination?.page_size,
          total: res.payload.pagination?.total_items,
        });
      }
    }
  };

  const [prevPage, setPrevPage] = useState<number>(1);
  const [prevEmployeeId, setPrevEmployeeId] = useState<number | undefined>(
    undefined,
  );

  return (
    <div>
      {width > 985 ? <Header /> : <MobileHeader />}
      <div className={s.header}>
        <div className={s.top_header}>
          <div className={s.h2}></div>
          <div className={s.search_block}>
            {
              <Search
                placeholder={t("Поиск")}
                allowClear
                enterButton
                size="small"
                onSearch={onSearch}
                className={s.search}
              />
            }
          </div>
        </div>
      </div>
      <div className={s.content}>
        <Tabs
          defaultActiveKey="myApplications"
          type="card"
          activeKey={activeTab}
          onChange={(key) => onTabChange(key)}
        >
          <TabPane tab={t(`Поступившие заказы`)} key="otherApplications">
            <Table<TableType>
              bordered
              pagination={otherApplicationsPagination}
              columns={columns}
              className="table"
              rowClassName={(_, index) =>
                index % 2 === 0 ? "table-row-light" : "table-row-dark"
              }
              onChange={(pagination) => {
                if (pagination?.current) {
                  getNextPage(pagination.current);
                }
              }}
              dataSource={
                data &&
                data.map((it, index) => ({
                  key: it.id,
                  number: index + 1,
                  status: (
                    <div className={s.status_bar}>
                      {it.routes?.map((route) => (
                        <div key={route.id} className={s.status_route_item}>
                          <div className={s.status_bar_country}>
                            {route.transit_country_name}
                          </div>

                          <div className={s.status_services_list}>
                            {route.services?.map((srv) => (
                              <div
                                key={srv.id}
                                className={s.status_service_row}
                              >
                                <div className={s.status_bar_service}>
                                  {srv.service_name} ({srv.broker_name})
                                </div>
                                <div className={s.status_bar_status}>
                                  {getListStatusTag(srv.status, "sm", t)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                  name: it.broker_details?.company_name,
                  vehicle_number: it?.vehicle_number,
                  application_number: it?.application_number,
                  registration_date: moment(it?.created_at).format(
                    "DD.MM.YYYY - HH:mm",
                  ),
                  order_status: (
                    <div className={s.status_bar2}>
                      {it.routes?.map((route) => (
                        <div key={route.id} className={s.status_route_item2}>
                          <div className={s.status_bar_country}>
                            {route.transit_country_name}
                          </div>

                          <div className={s.status_services_list2}>
                            {route.services.every(
                              (s) => s.status === "completed",
                            ) ? (
                              <div className={s.completed}>{t("Завершен")}</div>
                            ) : route.services.every(
                                (s) => s.status === "new",
                              ) ? (
                              <div className={s.not_started}>
                                {t("Не начат")}
                              </div>
                            ) : (
                              <div className={s.in_proccess}>
                                {t("В процессе")}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                  payment:
                    it?.status === "registered" ||
                    it?.status === "incomplete" ||
                    it?.status === "corrected" ? (
                      <div> {"-"} </div>
                    ) : (
                      <div
                        onClick={() => {
                          getPaymentAmount(it.id);
                          dispatch(getExchangeRate());
                        }}
                      >
                        <Tooltip title={t("Просмотреть оплату")} key={2}>
                          <CreditCardOutlined style={{ color: "gray" }} />
                        </Tooltip>
                      </div>
                    ),
                  application: (
                    <Link
                      to={`/manager/application/${it?.id}`}
                      className={s.edit}
                      onClick={() => {
                        dispatch(getManagersApplicationDetails(it.id));
                      }}
                    >
                      <Tooltip title={t("Просмотр/Редактирование")} key={1}>
                        <EditOutlined />
                      </Tooltip>
                    </Link>
                  ),
                }))
              }
            />
          </TabPane>
          <TabPane tab={t(`Мои заказы`)} key="myApplications">
            <div className={s.form}>
              {width > 700 ? (
                <Table<TableType>
                  bordered
                  pagination={pagination}
                  columns={columns}
                  className="table"
                  rowClassName={(_, index) =>
                    index % 2 === 0 ? "table-row-light" : "table-row-dark"
                  }
                  onChange={(pagination) => {
                    if (pagination?.current) {
                      getNextPage(pagination.current);
                    }
                  }}
                  dataSource={
                    data &&
                    data.map((it, index) => ({
                      key: it.id,
                      number: index + 1,
                      status: (
                        <div className={s.status_bar}>
                          {it.routes?.map((route) => (
                            <div key={route.id} className={s.status_route_item}>
                              <div className={s.status_bar_country}>
                                {route.transit_country_name}
                              </div>

                              <div className={s.status_services_list}>
                                {route.services?.map((srv) => (
                                  <div
                                    key={srv.id}
                                    className={s.status_service_row}
                                  >
                                    <div className={s.status_bar_service}>
                                      {srv.service_name} ({srv.broker_name})
                                    </div>
                                    <div className={s.status_bar_status}>
                                      {getListStatusTag(srv.status, "sm", t)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ),
                      name: it.broker_details?.company_name,
                      application_number: it?.application_number,
                      vehicle_number: it?.vehicle_number,
                      registration_date: moment(it?.created_at).format(
                        "DD.MM.YYYY - HH:mm",
                      ),
                      order_status: (
                        <div className={s.status_bar2}>
                          {it.routes?.map((route) => (
                            <div
                              key={route.id}
                              className={s.status_route_item2}
                            >
                              <div className={s.status_bar_country}>
                                {route.transit_country_name}
                              </div>
                              <div className={s.status_services_list2}>
                                {route.services.every(
                                  (s) => s.status === "completed",
                                ) ? (
                                  <div className={s.completed}>
                                    {t("Завершен")}
                                  </div>
                                ) : route.services.every(
                                    (s) => s.status === "new",
                                  ) ? (
                                  <div className={s.not_started}>
                                    {t("Не начат")}
                                  </div>
                                ) : (
                                  <div className={s.in_proccess}>
                                    {t("В процессе")}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ),
                      application: (
                        <Link
                          to={`/manager/application/${it?.id}`}
                          className={s.edit}
                          onClick={() => {
                            dispatch(getManagersApplicationDetails(it.id));
                          }}
                        >
                          <Tooltip title={t("Просмотр/Редактирование")} key={1}>
                            <EditOutlined />
                          </Tooltip>
                        </Link>
                      ),
                    }))
                  }
                />
              ) : (
                <MobileApplicationsList />
              )}
            </div>
          </TabPane>
        </Tabs>
      </div>
      <Modal
        title={t("Декларации")}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isDeclaratonModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={"50%"}
        footer={[
          <Button
            key="download"
            type="primary"
            loading={loadingFiles}
            onClick={() => downloadDeclarationsAsZip()}
          >
            {t("Скачать")} ZIP
          </Button>,
        ]}
      >
        <Table<TableData>
          columns={[
            {
              title: "№",
              dataIndex: "number",
              align: "center",
              width: 50,
            },
            {
              title: t("LRN code"),
              dataIndex: "lrn_code",
              align: "center",
              width: 300,
            },
            {
              title: t("Декларация"),
              dataIndex: "declaration",
              align: "center",
              width: 300,
            },
            {
              title: t("Добавлено"),
              dataIndex: "date",
              align: "center",
              width: 300,
              render: (text) => (
                <div className={s.font} style={{ fontSize: "12px" }}>
                  {text}
                </div>
              ),
            },
            {
              title: t("Скачать"),
              dataIndex: "download",
              align: "center",
              width: 50,
            },
          ]}
          dataSource={declarations?.map((it, index) => ({
            number: index + 1,
            lrn_code: it.lrn_code,
            declaration: (
              <Link
                to={it.declaration_file}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Tooltip title={t("Декларация")} key={4}>
                  <PaperClipOutlined style={{ fontSize: "12px" }} />
                  &nbsp;{it.name}
                </Tooltip>
              </Link>
            ),
            key: it.id,
            date: moment(it.created_at).format("DD.MM.YYYY - HH:mm"),
            download: (
              <div onClick={() => downloadFile(it)}>
                <Tooltip title={t("Скачать файл")} key={4}>
                  <DownloadOutlined />
                </Tooltip>
              </div>
            ),
          }))}
          pagination={false}
        />
        {loadingFiles ? (
          <>
            <p>{t("Загружаем и упаковываем файлы")}...</p>
            <Progress
              percent={progress}
              status={progress < 100 ? "active" : "success"}
            />
          </>
        ) : (
          <p>
            {t("Нажмите “Скачать ZIP”, чтобы загрузить все файлы архивом")}.
          </p>
        )}
      </Modal>
      <Footer />
    </div>
  );
};

export default ManagersApplicationsList;
